import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import Loading from 'common/components/atoms/Loading';
import { scrollbarRounded } from 'common/styles/scrollbar';
import AttentionCard from './AttentionCard';

const INK_2 = '#6b6e66';
const INK_3 = '#999b95';

const BUCKETS = [
  { key: 'urgent', test: score => score >= 70, labelKey: 'assets.triage.urgency.urgent', labelDefault: 'Urgent', rangeKey: 'assets.triage.urgency.urgentRange', rangeDefault: 'score ≥ 70', color: '#d92b2b' },
  { key: 'elevated', test: score => score >= 40 && score < 70, labelKey: 'assets.triage.urgency.elevated', labelDefault: 'Elevated', rangeKey: 'assets.triage.urgency.elevatedRange', rangeDefault: '40 – 69', color: '#e8890c' },
  { key: 'routine', test: score => score < 40, labelKey: 'assets.triage.urgency.routine', labelDefault: 'Routine', rangeKey: 'assets.triage.urgency.routineRange', rangeDefault: '< 40', color: '#c9ccc4' }
];

// Issue-type grouping: highest-priority present reason decides an asset's group.
const ISSUE_GROUPS = [
  { key: 'alarms', labelKey: 'assets.criteria.alarms', labelDefault: 'Alarms', color: '#d92b2b' },
  { key: 'diagnostics', labelKey: 'assets.criteria.diagnostics', labelDefault: 'Diagnostics', color: '#e8890c' },
  { key: 'diagnostics_not_evaluable', labelKey: 'assets.criteria.diagnosticsNotEvaluable', labelDefault: "Diagnostics can't run", color: '#c9ccc4' },
  { key: 'health_score', labelKey: 'assets.criteria.healthScore', labelDefault: 'Health Score', color: '#e8890c' },
  { key: 'baselines_status', labelKey: 'assets.criteria.baselines', labelDefault: 'Baselines', color: '#c9ccc4' },
  { key: 'missing_asset_info', labelKey: 'assets.criteria.missingInfo', labelDefault: 'Missing Info', color: '#c9ccc4' },
  { key: 'tasks', labelKey: 'assets.criteria.tasks', labelDefault: 'Open Tasks', color: '#c9ccc4' }
];

// Stripped (non-analyst) roles only triage configuration/action issues, so their
// issue grouping is limited to those groups — matching the criteria strip.
const STRIPPED_ISSUE_KEYS = ['missing_asset_info', 'tasks'];

const issueGroupsFor = stripped => (
  stripped ? ISSUE_GROUPS.filter(g => STRIPPED_ISSUE_KEYS.includes(g.key)) : ISSUE_GROUPS
);

const primaryIssueKey = (item, groups) => {
  const reason = item.reason_for_attention || {};
  const match = (groups || ISSUE_GROUPS).find(g => reason[g.key] && reason[g.key].length > 0);
  return match ? match.key : null;
};

const INK = '#383838';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  position: relative;
  ${scrollbarRounded}
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
  padding: 8px 20px 40px;
`;

const BulkBar = styled.div`
  position: sticky;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 20px 8px;
  padding: 9px 12px;
  background: ${INK};
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(40, 42, 36, 0.18);
  color: white;
`;

const BulkCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  margin-right: 4px;
`;

const BulkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: white;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: 7px;
  padding: 5px 11px;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.18); }
  ${props => props.spacerBefore && 'margin-left: auto;'}
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 4px 7px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${INK_2};

  span.dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: ${props => props.color};
  }
  span.range {
    font-weight: 600;
    color: ${INK_3};
    text-transform: none;
    letter-spacing: 0;
  }
`;

// The group's card count. It reads immediately after a score range ("< 40"), and
// as plain text separated by a "·" it was misread as part of the number itself —
// "< 40 · 9" scanning as "< 40.9". The pill gives it its own shape so it cannot
// be read as a continuation of the range. Matches CriteriaStrip's count chip,
// which is the same idea one row above this list.
const GroupCount = styled.span`
  font-size: 11px;
  font-weight: 700;
  border-radius: 9px;
  min-width: 22px;
  text-align: center;
  padding: 0 7px;
  line-height: 17px;
  background: #eff0ee;
  color: ${INK_2};
  text-transform: none;
  letter-spacing: 0;
`;

const Empty = styled.div`
  padding: 56px 20px;
  text-align: center;
  color: ${INK_3};
  font-size: 13px;
`;

class AttentionQueue extends React.Component {
  scrollEl = null;

  containerRef = React.createRef();

  componentDidMount() {
    this.ensureScrollListener();
  }

  componentDidUpdate() {
    this.ensureScrollListener();
  }

  componentWillUnmount() {
    this.detachScrollListener();
  }

  // The queue's own container is the scroll region, so the detail pane stays
  // put while this list scrolls independently.
  getScrollEl = () => this.containerRef.current;

  ensureScrollListener = () => {
    const el = this.getScrollEl();
    if (el && el !== this.scrollEl) {
      this.detachScrollListener();
      el.addEventListener('scroll', this.handleScroll);
      this.scrollEl = el;
    }
  };

  detachScrollListener = () => {
    if (this.scrollEl) {
      this.scrollEl.removeEventListener('scroll', this.handleScroll);
      this.scrollEl = null;
    }
  };

  handleScroll = () => {
    const el = this.getScrollEl();
    if (!el) return;
    if ((el.scrollHeight - el.clientHeight) - el.scrollTop < 80) {
      const { hasMore, loading, loadMoreItems } = this.props;
      if (hasMore && !loading) loadMoreItems();
    }
  };

  renderCard = (item) => {
    const {
      onSelect, onToggleReviewed, selectedId, pickedIds,
      onTogglePick, onSnooze, onUnsnooze, onToggleWatch, onOpenAsset, canWatchlist, stripped
    } = this.props;
    return (
      <AttentionCard
        key={item.id}
        item={item}
        selected={selectedId === item.id}
        onSelect={onSelect}
        onToggleReviewed={onToggleReviewed}
        anySelected={pickedIds.length > 0}
        picked={pickedIds.indexOf(item.id) !== -1}
        onTogglePick={onTogglePick}
        onSnooze={onSnooze}
        onUnsnooze={onUnsnooze}
        onToggleWatch={onToggleWatch}
        onOpenAsset={onOpenAsset}
        canWatchlist={canWatchlist}
        stripped={stripped}
      />
    );
  };

  // Group counts describe the cards rendered beneath the header. While more
  // pages remain unloaded that is a lower bound, not a total, so it is suffixed
  // with "+" to stop it reading as "this is all the work there is".
  groupCount = (n) => {
    const { hasMore } = this.props;
    return hasMore ? `${n}+` : `${n}`;
  };

  renderGroups = (rows) => {
    const { groupBy, stripped, t } = this.props;

    if (groupBy === 'none') {
      return rows.map(this.renderCard);
    }

    if (groupBy === 'issue') {
      const groups = issueGroupsFor(stripped);
      const sections = groups.map((group) => {
        const groupItems = rows.filter(item => primaryIssueKey(item, groups) === group.key);
        if (groupItems.length === 0) return null;
        return (
          <React.Fragment key={group.key}>
            <GroupHeader color={group.color}>
              <span className="dot" />
              {t(group.labelKey, { defaultValue: group.labelDefault })}
              <GroupCount data-testid={`attention-group-count-${group.key}`}>
                {this.groupCount(groupItems.length)}
              </GroupCount>
            </GroupHeader>
            {groupItems.map(this.renderCard)}
          </React.Fragment>
        );
      });
      // Catch any rows that match no rendered group (e.g. stripped roles whose
      // group set is narrowed) so nothing silently disappears from the list.
      const leftovers = rows.filter(item => primaryIssueKey(item, groups) === null);
      if (leftovers.length > 0) {
        sections.push(
          <React.Fragment key="__other">
            <GroupHeader color="#c9ccc4">
              <span className="dot" />
              {t('assets.triage.otherGroup', { defaultValue: 'Other' })}
              <GroupCount data-testid="attention-group-count-other">
                {this.groupCount(leftovers.length)}
              </GroupCount>
            </GroupHeader>
            {leftovers.map(this.renderCard)}
          </React.Fragment>
        );
      }
      return sections;
    }

    return BUCKETS.map((bucket) => {
      const bucketItems = rows.filter(item => bucket.test(Number(item.urgency_score) || 0));
      if (bucketItems.length === 0) return null;
      // The header counts the cards rendered beneath it, and nothing else. The
      // attention summary's by_urgency cannot be used: it is fetched with
      // attention_criteria stripped (see getAttentionSummary in
      // machines.actions.js) and computed over the unpaginated list, so its
      // scope is not this list's scope.
      //
      // While further pages remain unloaded the count is a lower bound, so it is
      // rendered as "50+" rather than a bare "50" that would read as a total.
      // A true total needs a criteria-honouring, per-bucket count from the
      // summary endpoint, which the API does not currently expose.
      const headerCount = this.groupCount(bucketItems.length);
      return (
        <React.Fragment key={bucket.key}>
          <GroupHeader color={bucket.color}>
            <span className="dot" />
            {t(bucket.labelKey, { defaultValue: bucket.labelDefault })}
            <span className="range">
              {t(bucket.rangeKey, { defaultValue: bucket.rangeDefault })}
            </span>
            <GroupCount data-testid={`attention-group-count-${bucket.key}`}>
              {headerCount}
            </GroupCount>
          </GroupHeader>
          {bucketItems.map(this.renderCard)}
        </React.Fragment>
      );
    });
  };

  render() {
    const {
      items, loading, emptyMsg, canWatchlist, pickedIds,
      onClearSelection, onBulkReview, onBulkSnooze, onBulkWatch, t
    } = this.props;
    const rows = items && items.object ? items.object : [];
    const pickedCount = pickedIds.length;

    return (
      <Container innerRef={(node) => { this.containerRef.current = node; }}>
        <ContentArea>
          {!loading && rows.length === 0 && <Empty>{emptyMsg}</Empty>}
          {this.renderGroups(rows)}
          {loading && <Loading />}
        </ContentArea>
        {pickedCount > 0 && (
          <BulkBar>
            <BulkCount>
              {`${pickedCount} ${t('assets.triage.selected', { defaultValue: 'selected' })}`}
            </BulkCount>
            <BulkBtn type="button" onClick={onBulkReview}>
              {`✓ ${t('assets.triage.markReviewed', { defaultValue: 'Mark Reviewed' })}`}
            </BulkBtn>
            {canWatchlist && (
              <BulkBtn type="button" onClick={onBulkWatch}>
                {`★ ${t('assets.triage.addWatch', { defaultValue: 'Add to watchlist' })}`}
              </BulkBtn>
            )}
            <BulkBtn type="button" onClick={onBulkSnooze}>
              {`🕐 ${t('assets.triage.snoozeAction', { defaultValue: 'Snooze' })}`}
            </BulkBtn>
            <BulkBtn type="button" spacerBefore onClick={onClearSelection}>
              {t('assets.triage.clear', { defaultValue: 'Clear' })}
            </BulkBtn>
          </BulkBar>
        )}
      </Container>
    );
  }
}

AttentionQueue.propTypes = {
  items: PropTypes.object,
  groupBy: PropTypes.oneOf(['urgency', 'issue', 'none']),
  onSelect: PropTypes.func.isRequired,
  onToggleReviewed: PropTypes.func.isRequired,
  selectedId: PropTypes.number,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  loadMoreItems: PropTypes.func,
  emptyMsg: PropTypes.string,
  canWatchlist: PropTypes.bool,
  stripped: PropTypes.bool,
  pickedIds: PropTypes.arrayOf(PropTypes.number),
  onTogglePick: PropTypes.func,
  onClearSelection: PropTypes.func,
  onBulkReview: PropTypes.func,
  onBulkSnooze: PropTypes.func,
  onBulkWatch: PropTypes.func,
  onSnooze: PropTypes.func,
  onUnsnooze: PropTypes.func,
  onToggleWatch: PropTypes.func,
  onOpenAsset: PropTypes.func,
  t: PropTypes.func.isRequired
};

AttentionQueue.defaultProps = {
  items: { object: [] },
  groupBy: 'urgency',
  selectedId: null,
  loading: false,
  hasMore: false,
  loadMoreItems: () => {},
  emptyMsg: 'No assets need attention',
  canWatchlist: false,
  stripped: false,
  pickedIds: [],
  onTogglePick: () => {},
  onClearSelection: () => {},
  onBulkReview: () => {},
  onBulkSnooze: () => {},
  onBulkWatch: () => {},
  onSnooze: () => {},
  onUnsnooze: () => {},
  onToggleWatch: () => {},
  onOpenAsset: () => {}
};

// Unwrapped class for unit tests, which supply `t` directly. Named distinctly
// from the default export so consumers importing the translated component are
// not flagged by import/no-named-as-default.
export { AttentionQueue as AttentionQueueBase };
export default withTranslation()(AttentionQueue);
